<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { AUTHORIZATION_AUTH_TYPES, RequestSection } from '@/utils/apiTab'
import { useApiTabStore } from '@/stores/apiTab.store'
import BasicAuth from '@/components/ide/ide/tabs/ApiTabComp/AuthorizationTab/BasicAuth.vue'
import BearerToken from '@/components/ide/ide/tabs/ApiTabComp/AuthorizationTab/BearerToken.vue'
import JwtBearer from '@/components/ide/ide/tabs/ApiTabComp/AuthorizationTab/JwtBearer.vue'
import { FORM_SELECT_VALUE_TYPE } from '@/components/forms/form.enum'
import BaseSelect from '@/components/forms/BaseSelect.vue'
const props = defineProps({
  activeRequestIndex: {
    type: Number,
    required: true
  }
})

const apiTabStore = useApiTabStore()

const authTypes = Object.values(AUTHORIZATION_AUTH_TYPES)

const selectedAuthType = ref<string>(AUTHORIZATION_AUTH_TYPES.NO_AUTH)
/**
 * Retrieves the authorization data for the active request from the API tab store.
 * If the authorization data exists, it sets the `selectedAuthType` to the specific authorization type.
 * If no data is found, it sets the `selectedAuthType` to `NO_AUTH`.
 */
const retrieveValuesFromStore = () => {
  const basisAuthData = apiTabStore.getRequestFieldData(
    props.activeRequestIndex,
    RequestSection.AUTHORIZATION,
    null
  )
  if (basisAuthData) {
    selectedAuthType.value = basisAuthData as AUTHORIZATION_AUTH_TYPES
  } else {
    selectedAuthType.value = AUTHORIZATION_AUTH_TYPES.NO_AUTH
  }
}

onMounted(() => {
  retrieveValuesFromStore()
})

watch(
  () => selectedAuthType.value,
  () => {
    apiTabStore.updateRequestField(
      props.activeRequestIndex,
      RequestSection.AUTHORIZATION,
      selectedAuthType.value,
      null
    )
  }
)

watch(
  () => props.activeRequestIndex,
  () => {
    retrieveValuesFromStore()
  }
)
</script>

<template>
  <div class="mx-auto flex w-full max-w-full flex-col gap-10">
    <div class="flex h-10 w-full flex-col items-start justify-start gap-2">
      <div class="text-high text-xs font-medium">Auth Type</div>
      <div class="w-full">
        <BaseSelect
          :list="authTypes"
          :valueType="FORM_SELECT_VALUE_TYPE.NAME"
          v-model="selectedAuthType"
          customClass="text-high block h-9 w-full appearance-none rounded-md background-primary tracking-tight !p-2 outline-none border !border-border-primary"
          :showLable="false"
          :getSelectedByItem="true"
          :isLightGray="false"
        />
      </div>
    </div>

    <div class="flex h-full min-h-fit w-full items-center justify-center">
      <div v-if="selectedAuthType == AUTHORIZATION_AUTH_TYPES.NO_AUTH">
        <span class="text-mid text-xs font-extralight tracking-tight">
          This request does not use any authorization.
          <a href="https://www.jdoodle.com/docs/ide/api-testing" target="_blank" class="underline"
            >Learn more about authorization.</a
          >
        </span>
      </div>
      <div
        class="flex h-full w-full flex-col gap-4"
        v-if="selectedAuthType == AUTHORIZATION_AUTH_TYPES.BASIC_AUTH"
      >
        <BasicAuth :activeRequestIndex="props.activeRequestIndex" />
      </div>
      <div
        class="flex h-full w-full flex-col gap-4"
        v-if="selectedAuthType == AUTHORIZATION_AUTH_TYPES.BEARER_TOKEN"
      >
        <BearerToken :activeRequestIndex="props.activeRequestIndex" />
      </div>
      <div
        class="flex h-full w-full flex-col gap-4"
        v-if="selectedAuthType == AUTHORIZATION_AUTH_TYPES.JWT_BEARER"
      >
        <JwtBearer :activeRequestIndex="props.activeRequestIndex" />
      </div>
    </div>
  </div>
</template>
